// Eigentümerportal (Portale E6/E7): Objekt-Umschalter, Übersicht, Einheiten, Finanzen (Soll), Rechnungen, Dokumente.
// #584: bei mehreren Objekten zuerst die Vorauswahl (ObjektAuswahl), danach Übersicht und Bereiche.
// Daten nur über die Portal-Funktionen (lib/portal/eigentuemerApi); geschrieben wird nur die Rechnungs-Entscheidung.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Buildings, ChartLineUp, ChatCircleText, CircleNotch, Door, FolderSimple, SealCheck, SquaresFour, UsersThree, Wrench } from '@phosphor-icons/react';
import AnfrageSheet from '../../components/portal/AnfrageSheet';
import { ladeNachrichten, sendeNachricht, stelleAnfrage, type PortalNachricht } from '../../lib/portal/nachrichtenApi';
import PortalRahmen, { usePortalTab } from '../../components/portal/PortalRahmen';
import { FehlerHinweis, LeerZustand } from '../../components/portal/bausteine';
import { eigentuemerTabs, type EigentuemerTab } from '../../lib/portal/tabs';
import { ART_LABEL, artIstWeg, auswahlZeigen, begruessung, objektSicht, tabOptionen, ueberZeile, type EigDaten, type ObjektSicht } from '../../lib/portal/eigentuemer';
import {
  auswahlGetroffen, entscheideRechnung, gemerktesObjekt, merkeAuswahl, ladeEigentuemerDaten, ladeEigentuemerDokumente, ladeEigentuemerTermine, ladeMeineRechnungen, merkeObjekt,
  ladeEinsaetze, ladeEinsatzDetail, ladeEinsatzFoto, ladeKostenStand, entscheideKosten,
} from '../../lib/portal/eigentuemerApi';
import { ladeDokumentHerunter } from '../../lib/portal/mieterApi';
import { fuerObjekt, offeneAnzahl, type EigDokument, type EigTermin, type PortalRechnung, type RechnungFilter, type RechnungStand } from '../../lib/portal/eigentuemerE7';
import type { PortalProfil } from '../../components/portal/PortalWeiche';
import ObjektUmschalter from './ObjektUmschalter';
import ObjektAuswahl from './ObjektAuswahl';
import EigentuemerUebersicht from './EigentuemerUebersicht';
import EigentuemerEinheiten from './EigentuemerEinheiten';
import EigentuemerFinanzen from './EigentuemerFinanzen';
import EigentuemerRechnungen from './EigentuemerRechnungen';
import EigentuemerDokumente from './EigentuemerDokumente';
import EigentuemerEinsaetze from './EigentuemerEinsaetze';
import { einsatzFilter, tabZaehler, type EinsatzDetail, type EinsatzKurz, type KostenStand } from '../../lib/portal/einsaetze';

const SYMBOLE = { uebersicht: SquaresFour, einheiten: Door, einsaetze: Wrench, finanzen: ChartLineUp, rechnungen: SealCheck, versammlung: UsersThree, dokumente: FolderSimple };

const OBERZEILE: Record<EigentuemerTab, string> = {
  uebersicht: '',
  einheiten: 'Einheiten und Vermietung',
  einsaetze: 'Schäden, Einsätze und Anfragen',
  finanzen: 'Finanzen',
  rechnungen: 'Rechnungen',
  versammlung: 'Eigentümerversammlung und Beschlüsse',
  dokumente: 'Unterlagen zum Objekt',
};

/** Datenquelle – nur die lokale Vorschau setzt hier Demo-Daten ein. */
export interface EigQuelle {
  daten: () => Promise<EigDaten | null>;
  rechnungen: () => Promise<PortalRechnung[]>;
  termine: () => Promise<EigTermin[]>;
  dokumente: () => Promise<EigDokument[]>;
  entscheiden: (id: string, e: Exclude<RechnungStand, 'offen'>, grund?: string) => Promise<void>;
  herunterladen: (d: { id: string; titel: string; datei_name: string | null }) => Promise<void>;
  einsaetze: () => Promise<EinsatzKurz[]>;
  einsatzDetail: (id: string) => Promise<EinsatzDetail | null>;
  einsatzFoto: (id: string) => Promise<string>;
  nachrichten: (id: string) => Promise<PortalNachricht[]>;
  nachrichtSenden: (id: string, text: string) => Promise<void>;
  anfrage: (betreff: string, text: string, objektId: string) => Promise<{ id: string; ticket_nummer: string }>;
  kostenStand: (id: string) => Promise<KostenStand | null>;
  kostenEntscheiden: (id: string, e: 'freigegeben' | 'abgelehnt', grund?: string) => Promise<void>;
}

const ECHT: EigQuelle = {
  daten: ladeEigentuemerDaten, rechnungen: ladeMeineRechnungen, termine: ladeEigentuemerTermine, dokumente: ladeEigentuemerDokumente,
  entscheiden: entscheideRechnung, herunterladen: ladeDokumentHerunter,
  einsaetze: ladeEinsaetze, einsatzDetail: ladeEinsatzDetail, einsatzFoto: ladeEinsatzFoto,
  nachrichten: ladeNachrichten, nachrichtSenden: sendeNachricht, anfrage: stelleAnfrage,
  kostenStand: ladeKostenStand, kostenEntscheiden: entscheideKosten,
};

export default function EigentuemerPortal({ profil, quelle = ECHT }: { profil: PortalProfil; quelle?: EigQuelle }) {
  const [daten, setDaten] = useState<EigDaten | null>(null);
  const [rechnungen, setRechnungen] = useState<PortalRechnung[]>([]);
  const [termine, setTermine] = useState<EigTermin[]>([]);
  const [dokumente, setDokumente] = useState<EigDokument[]>([]);
  const [einsaetze, setEinsaetze] = useState<EinsatzKurz[]>([]);
  const [anfrageOffen, setAnfrageOffen] = useState(false);
  // T4: Stand der Kostenfreigabe je laufendem Vorgang (für Chip und Tab-Zähler)
  const [staende, setStaende] = useState<Record<string, KostenStand | null>>({});
  const einsaetzeNeu = useCallback(async (liste?: EinsatzKurz[]) => {
    const l = liste ?? await quelle.einsaetze();
    if (!liste) setEinsaetze(l);
    const laufend = l.filter((e) => einsatzFilter(e.status) !== 'erledigt');
    const st = await Promise.all(laufend.map(async (e) => [e.id, await quelle.kostenStand(e.id)] as const));
    setStaende(Object.fromEntries(st));
  }, [quelle]);
  const [filter, setFilter] = useState<RechnungFilter>('offen');
  const [laedt, setLaedt] = useState(true);
  const [objektId, setObjektId] = useState<string | null>(() => gemerktesObjekt());
  const [gewaehlt, setGewaehlt] = useState(() => auswahlGetroffen());
  const [heute] = useState(() => new Date().toISOString().slice(0, 10));

  useEffect(() => {
    let aktiv = true;
    void Promise.all([quelle.daten(), quelle.rechnungen(), quelle.termine(), quelle.dokumente(), quelle.einsaetze()]).then(([d, r, t, dok, ein]) => {
      if (!aktiv) return;
      setDaten(d); setRechnungen(r); setTermine(t); setDokumente(dok); setEinsaetze(ein); setLaedt(false);
      void einsaetzeNeu(ein);
    });
    return () => { aktiv = false; };
  }, [quelle, einsaetzeNeu]);

  const rechnungenNeu = useCallback(() => { void quelle.rechnungen().then(setRechnungen); }, [quelle]);

  const objekte = useMemo<ObjektSicht[]>(
    () => (daten?.objekte ?? []).map((o) => objektSicht(daten!, o.id, heute)).filter((s): s is ObjektSicht => !!s),
    [daten, heute],
  );
  const sicht = objekte.find((o) => o.objekt.id === objektId) ?? objekte[0] ?? null;
  const tabs = eigentuemerTabs(tabOptionen(sicht?.objekt ?? null));
  const [tab, setTab] = usePortalTab(tabs, '/portal');
  const name = profil.full_name || [daten?.eigentuemer.vorname, daten?.eigentuemer.nachname].filter(Boolean).join(' ') || daten?.eigentuemer.firma_name || 'Eigentümer';

  const waehle = (id: string) => { setObjektId(id); merkeObjekt(id); setFilter('offen'); setGewaehlt(true); merkeAuswahl(true); };
  const zurAuswahl = () => { setGewaehlt(false); merkeAuswahl(false); setTab(tabs[0].key); };
  const auswahl = !laedt && !!daten && auswahlZeigen(objekte.length, gewaehlt);

  const inhalt = () => {
    if (laedt) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '80px 0', color: 'var(--p-w40)' }} aria-busy="true" aria-label="Lädt">
          <CircleNotch size={24} weight="bold" className="animate-spin" />
        </div>
      );
    }
    if (!daten) {
      return <FehlerHinweis text="Ihre Objektdaten konnten nicht geladen werden. Bitte versuchen Sie es später noch einmal oder wenden Sie sich an die Verwaltung." />;
    }
    if (!sicht) return <LeerZustand icon={Buildings} text="Ihnen ist zurzeit kein Objekt zugeordnet. Bitte wenden Sie sich an die Verwaltung." />;
    if (auswahl) {
      return <ObjektAuswahl objekte={objekte} daten={daten} oben={begruessung(daten.eigentuemer)} onWahl={waehle}
        hinweise={(id) => ({ rechnungen: offeneAnzahl(rechnungen, id), einsaetze: tabZaehler(einsaetze, id, staende) })} />;
    }

    const oben = tab === 'uebersicht' ? begruessung(daten.eigentuemer) : OBERZEILE[tab];
    const weg = artIstWeg(sicht.art);
    return (
      <div className="p-rein" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12, padding: '6px 4px 0', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--p-w45)' }}>{oben}</span>
            <ObjektUmschalter objekte={objekte} aktiv={sicht} onWahl={waehle} onAlle={zurAuswahl}
              ueber={(o) => ueberZeile(daten, o.objekt)} />
          </div>
          <span className={`ep-art ${weg ? 'ep-art-weg' : ''}`} data-tip={weg ? 'Wohnungseigentümergemeinschaft' : 'Wir verwalten das Haus und die Mietverhältnisse für Sie'}>
            {ART_LABEL[sicht.art]}
          </span>
        </div>
        {tab === 'uebersicht' && (
          <EigentuemerUebersicht sicht={sicht} objektEig={daten.objekt_eig} onTab={setTab}
            rechnungen={rechnungen.filter((r) => r.objekt_id === sicht.objekt.id)}
            termine={fuerObjekt(termine, sicht.objekt.id)} hatDokumente={fuerObjekt(dokumente, sicht.objekt.id).length > 0}
            einsaetze={einsaetze.filter((e) => e.objekt_id === sicht.objekt.id)} />
        )}
        {tab === 'einheiten' && <EigentuemerEinheiten sicht={sicht} />}
        {tab === 'einsaetze' && <EigentuemerEinsaetze einsaetze={einsaetze} objektId={sicht.objekt.id} staende={staende} onGeaendert={() => void einsaetzeNeu()}
          quelle={{ detail: quelle.einsatzDetail, foto: quelle.einsatzFoto, nachrichten: quelle.nachrichten, senden: quelle.nachrichtSenden,
            kostenStand: quelle.kostenStand, kostenEntscheiden: quelle.kostenEntscheiden }} />}
        {tab === 'finanzen' && <EigentuemerFinanzen daten={daten} sicht={sicht} heute={heute} />}
        {tab === 'rechnungen' && (
          <EigentuemerRechnungen rechnungen={rechnungen} objektId={sicht.objekt.id} filter={filter} onFilter={setFilter} onGeaendert={rechnungenNeu}
            aktionen={{
              entscheiden: async (id, e, grund) => {
                await quelle.entscheiden(id, e, grund);
                // sofort richtig anzeigen; das Nachladen (onGeaendert) bestätigt den Stand aus der Datenbank
                setRechnungen((alt) => alt.map((x) => (x.id === id ? { ...x, stand: e, entschieden_am: new Date().toISOString(), begruendung: grund?.trim() || null } : x)));
              },
              ansehen: (r) => quelle.herunterladen({ id: r.beleg_id ?? '', titel: `Rechnung ${r.rechnungsnummer ?? ''}`.trim(), datei_name: null }),
            }} />
        )}
        {tab === 'dokumente' && <EigentuemerDokumente dokumente={fuerObjekt(dokumente, sicht.objekt.id)} herunterladen={quelle.herunterladen} />}
      </div>
    );
  };

  return (
    <PortalRahmen
      portal="Eigentümerportal"
      tabs={tabs}
      ohneTabs={auswahl}
      symbole={SYMBOLE}
      aktiv={tab}
      onTab={setTab}
      anzahl={sicht && !auswahl ? {
        ...(tab !== 'rechnungen' ? { rechnungen: offeneAnzahl(rechnungen, sicht.objekt.id) } : {}),
        ...(tab !== 'einsaetze' ? { einsaetze: tabZaehler(einsaetze, sicht.objekt.id, staende) } : {}),
      } : undefined}
      profil={{ name, email: profil.email ?? '' }}
      profilTip={name}
      aktion={sicht && !auswahl ? (
        <button type="button" className="p-knopf p-knopf-gold mp-cta" onClick={() => setAnfrageOffen(true)}
          data-tip="Eine Frage an die Verwaltung schicken – die Antwort kommt hier im Portal" aria-label="Frage an die Verwaltung" style={{ height: 40, borderRadius: 20, fontSize: 13 }}>
          <ChatCircleText size={16} weight="bold" /><span className="mp-cta-text">Frage stellen</span>
        </button>
      ) : undefined}
    >
      {inhalt()}
      {anfrageOffen && sicht && (
        <AnfrageSheet unterzeile={sicht.name} listeName="Vorgänge"
          senden={(b, t) => quelle.anfrage(b, t, sicht.objekt.id)}
          onSchliessen={() => setAnfrageOffen(false)}
          onGesendet={() => { void einsaetzeNeu(); }}
          onZuListe={() => { setAnfrageOffen(false); setTab('einsaetze'); }} />
      )}
    </PortalRahmen>
  );
}
